import React from "react";
import { Link } from "react-router-dom";
import { InfoTip } from "./parts";
import { ChartTips } from "./ChartTip";

type CardTag = "section" | "div" | "article";

export interface CardProps {
  children: React.ReactNode;
  id?: string;
  as?: CardTag;
  /** The card holds a table: it scrolls sideways and measures its own width. */
  table?: boolean;
  "aria-label"?: string;
  /** Inside `Columns phoneOrder`: this card's place in the single phone
   *  column (mock `.more` keeps page order under 960px). */
  phoneOrder?: number;
}

/** Card (mock `.card`): white, 1px hairline, 14px radius. Every chart,
 *  table and list on a v2 page sits in one. */
export const Card: React.FC<CardProps> = ({
  children,
  id,
  as: Tag = "section",
  table,
  phoneOrder,
  ...rest
}) => (
  <Tag
    className="ds-card"
    id={id}
    data-table={table ? "" : undefined}
    data-phone-order={phoneOrder}
    aria-label={rest["aria-label"]}
  >
    {children}
  </Tag>
);

export interface CardHeaderProps {
  title: React.ReactNode;
  /** The (i) after the title: the explanation, in the tooltip. */
  info?: string;
  /** One line under the title (mock chart-h `.say`). With it, the header
   *  top-aligns and the right slot stacks (mock `.chart-tools`). */
  subtitle?: React.ReactNode;
  /** Right side: a hint, a link, a control. A string renders as a hint. */
  right?: React.ReactNode;
  /** Heading level. Default h2. */
  level?: 2 | 3;
}

/** CardHeader (mock `.card-h`, or `.chart-h` with a subtitle). */
export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  info,
  subtitle,
  right,
  level = 2,
}) => {
  const H = level === 3 ? "h3" : "h2";
  const heading = (
    <H>
      {title}
      {info && <InfoTip text={info} />}
    </H>
  );
  const r =
    typeof right === "string" ? (
      <span className="ds-hint">{right}</span>
    ) : (
      right
    );
  if (subtitle == null) {
    return (
      <div className="ds-card-h">
        {heading}
        {r}
      </div>
    );
  }
  return (
    <div className="ds-card-h" data-sub="">
      <div>
        {heading}
        <p className="ds-card-say">{subtitle}</p>
      </div>
      {r != null && <div className="ds-card-tools">{r}</div>}
    </div>
  );
};

/** A card's footer link, right-aligned (mock `.cfoot`). */
export const CardFooter: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="ds-card-foot">{children}</div>;

/** A card's chart area (mock `.chart-body`). */
export const CardBody: React.FC<{
  children: React.ReactNode;
  chart?: boolean;
}> = ({ children, chart }) =>
  chart ? (
    // A chart body is also the chart's hover-tip host (ChartTip.tsx).
    <ChartTips className="ds-card-body ds-chart">{children}</ChartTips>
  ) : (
    <div className="ds-card-body">{children}</div>
  );

/** A small muted paragraph inside a card (mock `.note`). */
export const CardNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <p className="ds-note">{children}</p>;

/** A whole card that is one link row (mock sales `.link`): a hatched mark,
 *  the sentence, and "See … →" on the right. */
export const RowLink: React.FC<{
  to: string;
  id?: string;
  children: React.ReactNode;
  cta: React.ReactNode;
}> = ({ to, id, children, cta }) => (
  <Link className="ds-card ds-rowlink" id={id} to={to}>
    <i className="ds-rowlink-mark" aria-hidden="true" />
    <span>{children}</span>
    <span className="ds-show">{cta}</span>
  </Link>
);

export default Card;
